<script setup>
import VisitsListErrorState from './VisitsListErrorState.vue';
import VisitsListSkeleton from './VisitsListSkeleton.vue';

defineProps({
  loading: { type: Boolean, default: false },
  error: { type: Boolean, default: false },
  loadingState: { type: Object, default: () => ({}) },
  emptyState: { type: Object, default: () => ({}) },
  table: { type: Boolean, default: false },
  colspan: { type: [Number, String], default: 1 },
});

const emit = defineEmits(['retry']);
</script>

<template>
  <tr v-if="table" class="border-b border-lais-border-soft">
    <td :colspan="colspan" :class="loading ? 'py-6' : 'py-16 text-center'">
      <div
        v-if="loading"
        class="grid gap-3"
        role="status"
        aria-live="polite"
        aria-busy="true"
        :data-visit-bucket="loadingState.bucket"
        data-visit-bucket-state="loading"
        :data-visit-bucket-fixture="loadingState.fixture"
        :data-visit-bucket-screenshot="loadingState.screenshot"
      >
        <div
          class="rounded-lais border border-lais-border bg-lais-bg-soft px-4 py-3 text-center"
        >
          <p class="text-[13px] font-semibold text-lais-text">
            {{ loadingState.title }}
          </p>
          <p class="mt-1 text-[12px] text-lais-text-muted">
            {{ loadingState.description }}
          </p>
        </div>
        <VisitsListSkeleton />
      </div>

      <VisitsListErrorState
        v-else-if="error"
        :title="emptyState.title"
        :description="emptyState.description"
        :data-visit-bucket="emptyState.bucket"
        data-visit-bucket-state="error"
        :data-visit-bucket-fixture="emptyState.fixture"
        :data-visit-bucket-screenshot="emptyState.screenshot"
        @retry="emit('retry')"
      />

      <div
        v-else
        role="status"
        aria-live="polite"
        :data-visit-bucket="emptyState.bucket"
        data-visit-bucket-state="empty"
        :data-visit-bucket-fixture="emptyState.fixture"
        :data-visit-bucket-screenshot="emptyState.screenshot"
      >
        <span
          class="mb-2 inline-block size-8"
          :class="[emptyState.icon, emptyState.iconClass]"
        />
        <p class="text-[13px] font-semibold text-lais-text-body">
          {{ emptyState.title }}
        </p>
        <p class="mt-1 text-[12px] text-lais-text-muted">
          {{ emptyState.description }}
        </p>
      </div>
    </td>
  </tr>

  <div
    v-else-if="loading"
    class="grid gap-3"
    role="status"
    aria-live="polite"
    aria-busy="true"
    :data-visit-bucket="loadingState.bucket"
    data-visit-bucket-state="loading"
    :data-visit-bucket-fixture="loadingState.fixture"
    :data-visit-bucket-screenshot="loadingState.screenshot"
  >
    <div
      class="rounded-lais border border-lais-border bg-lais-bg-soft px-4 py-3"
    >
      <p class="text-[13px] font-semibold text-lais-text">
        {{ loadingState.title }}
      </p>
      <p class="mt-1 text-[12px] text-lais-text-muted">
        {{ loadingState.description }}
      </p>
    </div>
    <VisitsListSkeleton />
  </div>

  <VisitsListErrorState
    v-else-if="error"
    :title="emptyState.title"
    :description="emptyState.description"
    :data-visit-bucket="emptyState.bucket"
    data-visit-bucket-state="error"
    :data-visit-bucket-fixture="emptyState.fixture"
    :data-visit-bucket-screenshot="emptyState.screenshot"
    @retry="emit('retry')"
  />

  <div
    v-else
    class="rounded-lais border border-lais-border py-16 text-center"
    role="status"
    aria-live="polite"
    :data-visit-bucket="emptyState.bucket"
    data-visit-bucket-state="empty"
    :data-visit-bucket-fixture="emptyState.fixture"
    :data-visit-bucket-screenshot="emptyState.screenshot"
  >
    <span
      class="mb-2 inline-block size-8"
      :class="[emptyState.icon, emptyState.iconClass]"
    />
    <p class="text-[14px] font-semibold text-lais-text-body">
      {{ emptyState.title }}
    </p>
    <p class="mt-1 text-[12px] text-lais-text-muted">
      {{ emptyState.description }}
    </p>
  </div>
</template>
